import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, resolveUserRole, homePathForRole } from '@/lib/supabase';
import AuthLayout from './AuthLayout';
import styles from './auth.module.css';

/**
 * Lands here from the password-recovery email. Supabase parses the token out of
 * the URL (detectSessionInUrl), which gives us a short-lived session that is
 * only good for setting a new password.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setReady(Boolean(data.session));
    })();

    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setReady(true);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const role = user ? await resolveUserRole(user.id) : null;
      navigate(role ? homePathForRole(role) : '/login', { replace: true });
    } catch {
      setError('Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  if (!ready) {
    return (
      <AuthLayout
        title="Lien expiré"
        subtitle="Ce lien de réinitialisation n'est plus valable. Demandez-en un nouveau."
        footer={<Link to="/forgot-password">Demander un nouveau lien</Link>}
      >
        <span />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Nouveau mot de passe" subtitle="Choisissez un nouveau mot de passe.">
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {error ? (
          <p className="alert alert--error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="field">
          <label className="label" htmlFor="password">
            Mot de passe
          </label>
          <input
            id="password"
            className="input"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={loading}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="confirm">
            Confirmer le mot de passe
          </label>
          <input
            id="confirm"
            className="input"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={loading}
          />
        </div>

        <button type="submit" className="btn btn--primary btn--block" disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer le mot de passe'}
        </button>
      </form>
    </AuthLayout>
  );
}
